import { useMemo } from 'react';
import { compareByConnection } from '../utils/websiteStatus';

// Orders a website list connected-first, for the Overview "My Websites" card,
// the Websites tab and the notification bell. One hook, one answer — the three
// screens cannot disagree.
//
// ── WHAT THIS USED TO DO, AND WHY IT NO LONGER DOES ─────────────────────────
// It used to query `user_generations` through Supabase and mark every website
// id appearing there `has_generations`, which utils/websiteStatus.js then read
// as "connected". A comment here asserted that the admin-server
// `last_connected_at` work "has been reverted", and on that basis the branch
// that used the real signal was deleted, leaving generations as the only one.
//
// That assertion was false. The column exists, migration 008 has run, the
// server stamps it on every authenticated plugin request, and it is present in
// the websites payload this hook receives. Status is therefore read straight
// off the row now — see utils/websiteStatus.js for the rule — and the extra
// query is gone, along with:
//
//   • three unrelated components each issuing it (the bell re-ran it every
//     three minutes),
//   • a failure mode where one failed query marked EVERY site disconnected,
//   • the loading beat it introduced before any status could be shown.
//
// Ordering stays here because it is the one place the status is guaranteed to
// be known for the whole list, so neither screen can forget to apply it.
//
// @returns {{ websites: Array, statusReady: boolean }}
//   `statusReady` no longer gates a query — the status arrives with the rows.
//   It is kept because callers fold it into their loading state, and it still
//   answers the question they are asking: false while there is no user yet and
//   the list cannot mean anything.

export function useWebsitesWithStatus(userId, websites) {
  // A fresh array before sorting: Array.prototype.sort mutates, and the caller
  // owns `websites` as React state.
  const ordered = useMemo(
    () => [...(websites || [])].sort(compareByConnection),
    [websites],
  );

  return { websites: ordered, statusReady: !!userId };
}
